import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../../ui-components/table/table";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSIcon from "../../../ui-components/icon/xgsIcon";
import XGSIcons from "../../../ui-components/icon/xgsIcons";
import Loading from "../../../ui-components/loading/loading";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import {
  getSubscriptions,
  subscriptionsSelector,
  unsubscribeProbillStatus,
  removeProbillStatusSubscriptionLocally
} from "../../../slices/subscriptions/subscriptionsSlice";
import SubscriptionsState from "../../../slices/subscriptions/SubscriptionsState";
import { userSelector } from "../../../slices/user/userSlice";
import UserState from "../../../slices/user/UserState";
import "./subscriptions.scss";

const columnHelper = createColumnHelper<any>();

const Subscriptions: React.FC<{}> = (props) => {
  const dispatch = useDispatch();
  const subscriptionsState: SubscriptionsState = useSelector(subscriptionsSelector);
  const userState: UserState = useSelector(userSelector);
  const [activeProbill, setActiveProbill] = useState<number | undefined>(undefined);

  const columns = [
    columnHelper.accessor("probill", {
      id: "probill",
      header: "Probill",
      cell: (cellProps) => (
        <Link
          className="xgs-table-link-cell"
          target="_blank"
          to={`${userState.activeSubAccount?.accountNumber ? "/" + userState.activeSubAccount?.accountNumber : ""}/shipments/${cellProps.getValue()}`}
        >
          {cellProps.getValue()}
        </Link>
      ),
      size: 95,
    }),
    columnHelper.accessor("destination.name", {
      id: "destination.name",
      header: "Destination",
      size: 145
    }),
    columnHelper.accessor("consignee.info", {
      id: "consignee.info",
      header: "Consignee",
      size: 150
    }),
    columnHelper.accessor("status.translate", {
      id: "status.translate",
      header: "Status",
      size: 130
    }),
    columnHelper.display({
      header: "",
      id: "actionColumn",
      cell: ({ row }) => (
        <div style={{ textAlign: "center" }}>
          <Button
            className="xgs-subscriptions__remove-button"
            type="button"
            theme={ButtonThemes.gray}
            onClick={(e) => {
              e.stopPropagation();
              removeSubscription(row.original.probill);
              setActiveProbill(row.original.probill);
            }}
            spinner={activeProbill === row.original.probill}
          >
            {activeProbill !== row.original.probill && (
              <XGSIcon
                icon={XGSIcons.faBellSlash}
                size="1x"
              />
            )}
          </Button>
        </div>
      ),
      size: 52,
    })
  ];

  const removeSubscription = (probill: number) => {
    dispatch(unsubscribeProbillStatus(probill, () => {
      dispatch(removeProbillStatusSubscriptionLocally(probill));
      toast.info(`You unsubscribed from status updates of probill ${probill}`, { autoClose: 3000 });
      mixpanel.track("Unsubscribed from probill status updates", {
        "Probill": probill
      });
    }));
  };

  useEffect(() => {
    dispatch(getSubscriptions());
  }, [dispatch]);

  return (
    <div className="xgs-subscriptions">
      <div className="xgs-subscriptions__header">Delivery statuses</div>
      {subscriptionsState.loading && (
        <div className="xgs-subscriptions__loading">
          <Loading isLoading={true} />
        </div>
      )}
      {subscriptionsState.subscriptions && subscriptionsState.subscriptions.length > 0 && !subscriptionsState.loading && (
        <div className="xgs-subscriptions__table">
          <Table
            columns={columns}
            data={subscriptionsState.subscriptions}
            rowHeight={64}
          />
        </div>
      )}
      {(!subscriptionsState.subscriptions || subscriptionsState.subscriptions.length === 0) && subscriptionsState.loaded && (
        <div className="xgs-subscriptions__no-data">There are no subscriptions.</div>
      )}
      {subscriptionsState.loadingFailed && (
        <XGSErrorMessage>
          {subscriptionsState.loadingError || "Error while loading subscriptions."}
        </XGSErrorMessage>
      )}
    </div>
  );
};

export default Subscriptions;
